import React, { useEffect, useState } from "react";
import Menu from "../panel/Menu";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Stack,
  MenuItem
} from "@mui/material";
import {
  Add,
  Edit,
  DeleteOutline
} from "@mui/icons-material";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";
import Swal from "sweetalert2";

function Rateplans() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [form, setForm] = useState({
    planCode: "",
    planName: "",
    description: "",
    status: "active"
  });

  /* ================= FETCH ================= */
  const fetchPlans = async () => {
    const snap = await getDocs(collection(db, "rate_plans"));
    setPlans(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  /* ================= SAVE ================= */
  const savePlan = async () => {
    if (!form.planCode || !form.planName) {
      Swal.fire("Required", "Plan Code & Name are required", "warning");
      return;
    }

    try {
      if (editId) {
        await updateDoc(doc(db, "rate_plans", editId), form);
        Swal.fire("Updated", "Rate plan updated", "success");
      } else {
        await addDoc(collection(db, "rate_plans"), {
          ...form,
          createdAt: serverTimestamp()
        });
        Swal.fire("Added", "Rate plan added", "success");
      }

      setOpen(false);
      resetForm();
      fetchPlans();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  /* ================= DELETE ================= */
  const deletePlan = async (id) => {
    const ok = await Swal.fire({
      title: "Delete rate plan?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete"
    });

    if (ok.isConfirmed) {
      await deleteDoc(doc(db, "rate_plans", id));
      Swal.fire("Deleted", "Rate plan removed", "success");
      fetchPlans();
    }
  };

  /* ================= HELPERS ================= */
  const resetForm = () => {
    setEditId(null);
    setForm({
      planCode: "",
      planName: "",
      description: "",
      status: "active"
    });
  };

  const openEdit = (plan) => {
    setEditId(plan.id);
    setForm({
      planCode: plan.planCode,
      planName: plan.planName,
      description: plan.description,
      status: plan.status
    });
    setOpen(true);
  };

  return (
    <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
      <Menu />

      <Box flex={1} p={4} className="content-wrapper">
        <Stack direction="row" justifyContent="space-between" mb={3}>
          <Typography fontSize={26} fontWeight={800}>
            Rate Plans
          </Typography>

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpen(true)}
          >
            Add Rate Plan
          </Button>
        </Stack>

        {/* ================= TABLE ================= */}
        <Card sx={{ borderRadius: 4 }}>
          <CardContent>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Plan Code</TableCell>
                    <TableCell>Plan Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {plans.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.planCode}</TableCell>
                      <TableCell>{p.planName}</TableCell>
                      <TableCell>{p.description}</TableCell>
                      <TableCell>
                        <Chip
                          label={p.status.toUpperCase()}
                          color={p.status === "active" ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => openEdit(p)}>
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => deletePlan(p.id)}
                        >
                          <DeleteOutline />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}

                  {!plans.length && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No rate plans found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* ================= DIALOG ================= */}
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editId ? "Edit Rate Plan" : "Add Rate Plan"}
          </DialogTitle>

          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label="Plan Code"
                value={form.planCode}
                onChange={(e) =>
                  setForm({ ...form, planCode: e.target.value })
                }
                fullWidth
              />

              <TextField
                label="Plan Name"
                value={form.planName}
                onChange={(e) =>
                  setForm({ ...form, planName: e.target.value })
                }
                fullWidth
              />

              <TextField
                label="Description"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                multiline
                rows={3}
                fullWidth
              />

              <TextField
                select
                label="Status"
                value={form.status}
                onChange={(e) =>
                  setForm({ ...form, status: e.target.value })
                }
                fullWidth
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>

          <DialogActions>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={savePlan}>
              {editId ? "Update" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

export default Rateplans;
